import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./index.css";
import EndOfLife42Header from "./Header";
import { ShowHide } from "../components/ShowHideProduct";
import { usePDFShowHide } from "../../../../hooks/usePDFShowHide";
import Flow42_1 from "./Flow42_1";
import Flow42_2 from "./Flow42_2";
import { getEndOfLife42LCA, updateEndOfLife42LCA } from "../../../../apis/Product/EndOfLifeFlow42Api";
import { AzilisSwitch } from "../../../../components/Switch";

const ProdEndOfLife42Components = ({ productId }) => {
  const { t } = useTranslation();
  const [show, setShow] = usePDFShowHide(false, {
    forceShowInPDF: true,
    pdfDelay: 1000,
  });

  const [formData, setFormData] = useState({
    products_waste_code_eak: "",
    is_product_classified_as_hazardous_waste: "No",
    portion_of_hazardous_toxic_radioactive_waste_weight_percent: "",
    material_recycling_possible: "No",
    specify_which_parts_recyclable: "",
    energy_recovery_possible: "No",
    restrictions_recommendations_material_energy_recovery: "No",
    specify_restrictions: "",
    system_boundary: "",
  });

  const [hasChanges, setHasChanges] = useState(false);

  const fetchData = useCallback(() => {
    if (productId) {
      getEndOfLife42LCA({
        productId,
        setData: (data) => {
          if (data) {
            setFormData({
              products_waste_code_eak: data.products_waste_code_eak || "",
              is_product_classified_as_hazardous_waste: data.is_product_classified_as_hazardous_waste || "No",
              portion_of_hazardous_toxic_radioactive_waste_weight_percent: data.portion_of_hazardous_toxic_radioactive_waste_weight_percent || "",
              material_recycling_possible: data.material_recycling_possible || "No",
              specify_which_parts_recyclable: data.specify_which_parts_recyclable || "",
              energy_recovery_possible: data.energy_recovery_possible || "No",
              restrictions_recommendations_material_energy_recovery: data.restrictions_recommendations_material_energy_recovery || "No",
              specify_restrictions: data.specify_restrictions || "",
              system_boundary: data.system_boundary || "",
            });
          }
        },
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateEndOfLife42LCA({
      productId,
      products_waste_code_eak: formData.products_waste_code_eak,
      is_product_classified_as_hazardous_waste: formData.is_product_classified_as_hazardous_waste,
      portion_of_hazardous_toxic_radioactive_waste_weight_percent: formData.portion_of_hazardous_toxic_radioactive_waste_weight_percent,
      material_recycling_possible: formData.material_recycling_possible,
      specify_which_parts_recyclable: formData.specify_which_parts_recyclable,
      energy_recovery_possible: formData.energy_recovery_possible,
      restrictions_recommendations_material_energy_recovery: formData.restrictions_recommendations_material_energy_recovery,
      specify_restrictions: formData.specify_restrictions,
      system_boundary: formData.system_boundary,
      callback: () => {
        setHasChanges(false);
        fetchData();
      },
      use_swal: true,
    });
  };

  const isHazardousYes = formData.is_product_classified_as_hazardous_waste === "Yes";
  const isMaterialRecyclingYes = formData.material_recycling_possible === "Yes";
  const isRestrictionsYes = formData.restrictions_recommendations_material_energy_recovery === "Yes";

  return (
    <div className="end-of-life-42-container" id="end-of-life-42-id">
      <EndOfLife42Header productId={productId} show={show} />
      <ShowHide show={show} setShow={setShow} />
      {show && (
        <div className="end-of-life-42-content">
          {/* System Boundary */}
          <div className="end-of-life-42-field-group end-of-life-42-field-group-wide">
            <div className="end-of-life-42-label">
              {t("SYSTEM_BOUNDARY") || "System boundary:"}
            </div>
            <input
              type="text"
              className="end-of-life-42-input"
              placeholder={t("WRITE_COMMENTS_SYSTEM_BOUNDARIES") || "Write comments on the system boundaries for this assessment. Include"}
              value={formData.system_boundary}
              onChange={(e) => handleInputChange("system_boundary", e.target.value)}
            />
          </div>

          {/* Form Fields Grid */}
          <div className="end-of-life-42-form-grid">
            {/* Left Column */}
            <div className="end-of-life-42-column">
              <div className="end-of-life-42-field-group">
                <div className="end-of-life-42-label">
                  {t("PRODUCTS_WASTE_CODE_EAK") || "Product's waste code (EAK)"}
                </div>
                <input
                  type="text"
                  className="end-of-life-42-input"
                  placeholder={t("USER_TYPES") || "User Types..."}
                  value={formData.products_waste_code_eak}
                  onChange={(e) => handleInputChange("products_waste_code_eak", e.target.value)}
                />
              </div>

              <div className="end-of-life-42-field-group">
                <div className="end-of-life-42-label">
                  {t("MATERIAL_RECYCLING_POSSIBLE") || "Material recycling possible for all or part of the product"}
                </div>
                <div className={`end-of-life-42-toggle-container ${formData.material_recycling_possible === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.material_recycling_possible}
                    setWhich={(value) => {
                      handleInputChange("material_recycling_possible", value);
                      if (value === "No") {
                        handleInputChange("specify_which_parts_recyclable", "");
                      }
                    }}
                  />
                </div>
              </div>

              {isMaterialRecyclingYes && (
                <div className="end-of-life-42-field-group">
                  <div className="end-of-life-42-label">
                    {t("SPECIFY_WHICH_PARTS_RECYCLABLE") || "Specify which parts of the product are recyclable"}
                  </div>
                  <input
                    type="text"
                    className="end-of-life-42-input"
                    placeholder={t("USER_TYPES") || "User Types..."}
                    value={formData.specify_which_parts_recyclable}
                    onChange={(e) => handleInputChange("specify_which_parts_recyclable", e.target.value)}
                  />
                </div>
              )}

              <div className="end-of-life-42-field-group">
                <div className="end-of-life-42-label">
                  {t("ENERGY_RECOVERY_POSSIBLE") || "Energy recovery possible for all or part of the product"}
                </div>
                <div className={`end-of-life-42-toggle-container ${formData.energy_recovery_possible === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.energy_recovery_possible}
                    setWhich={(value) => handleInputChange("energy_recovery_possible", value)}
                  />
                </div>
              </div>
            </div>

            {/* Middle Column */}
            <div className="end-of-life-42-column">
              <div className="end-of-life-42-field-group">
                <div className="end-of-life-42-label">
                  {t("IS_PRODUCT_HAZARDOUS_WASTE") || "Is the product classified as hazardous waste (Y/N)"}
                </div>
                <div className={`end-of-life-42-toggle-container ${formData.is_product_classified_as_hazardous_waste === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.is_product_classified_as_hazardous_waste}
                    setWhich={(value) => {
                      handleInputChange("is_product_classified_as_hazardous_waste", value);
                      if (value === "No") {
                        handleInputChange("portion_of_hazardous_toxic_radioactive_waste_weight_percent", "");
                      }
                    }}
                  />
                </div>
              </div>

              {isHazardousYes && (
                <div className="end-of-life-42-field-group">
                  <div className="end-of-life-42-label">
                    {t("PORTION_HAZARDOUS_WASTE_WEIGHT") || "Portion of hazardous / toxic / radioactive waste (weight%)"}
                  </div>
                  <input
                    type="text"
                    className="end-of-life-42-input"
                    placeholder={t("USER_TYPES") || "User Types..."}
                    value={formData.portion_of_hazardous_toxic_radioactive_waste_weight_percent}
                    onChange={(e) => handleInputChange("portion_of_hazardous_toxic_radioactive_waste_weight_percent", e.target.value)}
                  />
                </div>
              )}

              <div className="end-of-life-42-field-group">
                <div className="end-of-life-42-label">
                  {t("RESTRICTIONS_RECOMMENDATIONS") || "Restrictions and recommendations for material or energy recovery"}
                </div>
                <div className={`end-of-life-42-toggle-container ${formData.restrictions_recommendations_material_energy_recovery === "Yes" ? "yes-selected" : "no-selected"}`}>
                  <AzilisSwitch
                    choice1="Yes"
                    choice2="No"
                    styleNo={1}
                    which={formData.restrictions_recommendations_material_energy_recovery}
                    setWhich={(value) => {
                      handleInputChange("restrictions_recommendations_material_energy_recovery", value);
                      if (value === "No") {
                        handleInputChange("specify_restrictions", "");
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="end-of-life-42-column">
              {isRestrictionsYes && (
                <div className="end-of-life-42-field-group">
                  <div className="end-of-life-42-label">
                    {t("SPECIFY") || "Specify"}
                  </div>
                  <input
                    type="text"
                    className="end-of-life-42-input"
                    placeholder={t("USER_TYPES") || "User Types..."}
                    value={formData.specify_restrictions}
                    onChange={(e) => handleInputChange("specify_restrictions", e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {hasChanges && (
            <div className="end-of-life-42-save-container">
              <button className="end-of-life-42-save-btn" onClick={handleSave}>
                {t("SAVE") || "Save"}
              </button>
            </div>
          )}

          {/* Flow 42.1 */}
          <Flow42_1 productId={productId} />

          {/* Flow 42.2 */}
          <Flow42_2 productId={productId} />
        </div>
      )}
    </div>
  );
};

export default ProdEndOfLife42Components;

